import React, { useState, useEffect } from "react";
import useDataSourceManager from "hooks/useDataSourceManager";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from 'uuid';

// Component
import ModalContainer from "components/ModalContainer";
import GlobalAlert from "components/Notification";
import Loading from "./loading";

// Util
import { defaultImportCreditsObj, defaultLicenseKeyItem } from "./util/const";
import { toJSON } from "./util/func";
import Api from "./util/api";
import Form from "./util/form";
import MainView from "./MainView";
import UploadView from "./UploadView";
import ResultView from "./ResultView";

// Const
const viewStateEnum = {
  MAIN: "main",
  UPLOAD: "upload",
  RESULT: "result",
};

const getViewStateTitle = (view) => {
  const viewStateTitleMap = {
    [viewStateEnum.MAIN]: "Upload License Keys by Upload",
    [viewStateEnum.UPLOAD]: "Import License Key List",
    [viewStateEnum.RESULT]: "Import License Keys",
  }

  return viewStateTitleMap[view];
}

const ImportCreditsModal = ({
  auth,
  openModal,
  handleCloseModal,
  onSaveChanges,
}) => {
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const api = Api.init(auth);
  const form = Form.init(theme);

  // State
  const [loading, setLoading] = useState(false);
  const [formValue, setFormValue] = useState(defaultImportCreditsObj());
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const [viewState, setViewState] = useState(viewStateEnum.MAIN);
  const [requestUuid, setRequestUuid] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [results, setResults] = useState([]);

  // Method
  const init = () => {
    setFormValue(defaultImportCreditsObj());
    setViewState(viewStateEnum.MAIN);
    setSelectedIds([]);
    setResults([]);
  }

  const closeModal = () => {
    init();
    handleCloseModal();
  }

  const changeViewState = (newState) => {
    setViewState(newState);
  }

  const getViewTitle = (view) => {
    return getViewStateTitle(view);
  }

  const handleMainView = {
    addLicenseKey: () => {
      setFormValue(prev => ({
        ...prev,
        licenseKeys: [...prev.licenseKeys, defaultLicenseKeyItem()]
      }));
    },
    updateKeySegment: (rowIndex, segmentIndex, value) => {
      setFormValue(prev => {
        const newKeys = [...prev.licenseKeys];
        const newSegments = [...newKeys[rowIndex].keySegments];
        newSegments[segmentIndex] = value.toUpperCase().slice(0, 5);
        newKeys[rowIndex] = { ...newKeys[rowIndex], keySegments: newSegments };
        return { ...prev, licenseKeys: newKeys };
      });
    },
    pasteLicenseKey: (rowIndex, startSegIndex, cleanKey) => {
      setFormValue(prev => {
        const newKeys = [...prev.licenseKeys];
        const newSegments = [...newKeys[rowIndex].keySegments];
        const segmentsToPaste = cleanKey.match(/.{1,5}/g) || [];

        segmentsToPaste.forEach((seg, index) => {
          const targetIndex = startSegIndex + index;
          if (targetIndex < 4) {
            newSegments[targetIndex] = seg.toUpperCase().slice(0, 5);
          }
        });

        newKeys[rowIndex] = { ...newKeys[rowIndex], keySegments: newSegments };
        return { ...prev, licenseKeys: newKeys };
      });
    },
    removeLicenseKey: (id) => {
      setFormValue(prev => ({
        ...prev,
        licenseKeys: prev.licenseKeys.filter(item => item.id !== id)
      }));
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    },
    deleteSelectedIds: (idsToDelete) => {
      setFormValue(prev => ({
        ...prev,
        licenseKeys: prev.licenseKeys.filter(item => !idsToDelete.includes(item.id))
      }));
      setSelectedIds([]);
    },
    apply: () => {
      setLoading(true);
      const { apiKey, params } = api.getRequestParams();
      const payload = toJSON(formValue);
      const uuid = Math.random().toString(36).substring(7);
      setRequestUuid(uuid);

      sendRequest(
        apiKey,
        uuid,
        params,
        payload,
      );
    }
  };

  const handleUploadView = {
    closeView: () => setViewState(viewStateEnum.MAIN),
    importKeys: (replace, keys) => {
      setFormValue(prev => {
        const keysWithIds = keys.map(k => ({
          ...k,
          id: k.id || uuidv4()
        }));

        let finalKeys = [...prev.licenseKeys];

        if (replace) {
          keysWithIds.forEach(newKey => {
            const newKeyStr = newKey.keySegments.join("").toUpperCase();
            // Find existing keys that match
            const matchedIndexes = finalKeys.reduce((acc, existingKey, index) => {
              const existingKeyStr = existingKey.keySegments.join("").toUpperCase();
              if (existingKeyStr === newKeyStr) {
                acc.push(index);
              }
              return acc;
            }, []);

            // Remove existing matches
            if (matchedIndexes.length > 0) {
              for (let i = matchedIndexes.length - 1; i >= 0; i--) {
                finalKeys.splice(matchedIndexes[i], 1);
              }
            }
            // Add the new key
            finalKeys.push(newKey);
          });
        } else {
          finalKeys.push(...keysWithIds);
        }

        return { ...prev, licenseKeys: finalKeys };
      });
      setViewState(viewStateEnum.MAIN);
    }
  };

  // Side effect
  useEffect(() => {
    if (openModal) {
      init();
    }
  }, [openModal]);

  useEffect(() => {
    if (!requestUuid) return;
    const response = getData(requestUuid);

    if (response) {
      setLoading(false);

      // Handle Error Response
      if (response.code !== undefined && response.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: response.message || "Failed to import license keys",
        });
        setRequestUuid(null);
        return;
      }

      // Extract results - handle both { data: [] } and [] formats
      const responseResults = Array.isArray(response) ? response : (response.data || []);

      if (responseResults.length > 0) {
        setResults(responseResults);
        setViewState(viewStateEnum.RESULT);
        // Trigger background reload silently as soon as results are ready
        onSaveChanges(true);
      } else {
        // Successful API call but no data returned
        setAlertInfo({
          open: true,
          severity: "error",
          message: "No data returned from server",
        });
      }
      setRequestUuid(null);
    }
  }, [dataMap]);

  if (!openModal) {
    return null;
  }

  return (
    <>
      <ModalContainer display={openModal} modalWidth="800px">
        {loading && <Loading />}

        {viewState === viewStateEnum.RESULT ? (
          <ResultView
            viewStateEnum={viewStateEnum}
            getViewTitle={getViewTitle}
            form={form}
            results={results}
            closeModal={closeModal}
          />
        ) : (
          <MainView
            viewStateEnum={viewStateEnum}
            viewState={viewState}
            getViewTitle={getViewTitle}
            form={form}
            formValue={formValue}
            handleMainView={handleMainView}
            handleUploadView={handleUploadView}
            changeViewState={changeViewState}
            closeModal={closeModal}
            selectedIndexes={selectedIds}
            setSelectedIndexes={setSelectedIds}
          />
        )}
      </ModalContainer >

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default ImportCreditsModal;
